import type { Cue } from '@core/model/cue.ts';
import {
  createJob,
  getJob,
  mapSoraCreateFailureToLegacy,
  mapSoraJobToLegacy,
  shouldClearCachedSoraJobId,
  uploadAsrCache,
  type SoraAsrSource,
  type SoraAuthOpts,
  type SoraClientOptions,
} from './soraClient.ts';
import {
  memberJobProgressText,
  serviceDismissText,
} from '../../lib/serviceStatusMessages.ts';

export type MemberServiceCue = Cue & {
  info?: boolean;
  restored?: boolean;
  lang?: string;
};

export type MemberServiceResponse = {
  response?: string;
  result?: MemberServiceCue[];
  position?: number;
  progress?: number;
  desc?: string;
  phase?: 'download' | 'transcribe';
};

const memberJobIds = new Map<string, string>();
/** Completed encode results shared across main/second (extension poll fan-out parity). */
const memberJobResults = new Map<string, Cue[]>();
/** In-flight poll promises so dual-slot does not duplicate create/poll. */
const memberJobInflight = new Map<string, Promise<Cue[]>>();
/** Coalesce ASR cache uploads per video + asrSource. */
const asrUploadInflight = new Map<string, Promise<void>>();

/**
 * Bumped by clearMemberEnhanceState so pending poll loops from a prior
 * model/SPA session are ignored (extension memberBackend parity).
 */
let memberEnhanceEpoch = 0;

/** Snapshot for callers to abort after clearMemberEnhanceState. */
export function getMemberEnhanceEpoch(): number {
  return memberEnhanceEpoch;
}

function resolveAsrSourceBucket(asrSource?: SoraAsrSource): SoraAsrSource {
  return asrSource === 'creator' ? 'creator' : 'asr';
}

function normalizeCreatorLc(lc?: string | null): string {
  const s = (lc ?? '').trim().toLowerCase();
  return s || 'und';
}

function memberJobKey(
  endpoint: 'v' | 'v2' | 'v4',
  videoId: string,
  asrSource?: SoraAsrSource,
  lc?: string,
): string {
  const bucket = resolveAsrSourceBucket(asrSource);
  if (bucket === 'creator') {
    return `${endpoint}:${videoId}:${bucket}:${normalizeCreatorLc(lc)}`;
  }
  return `${endpoint}:${videoId}:${bucket}`;
}

export function resetMemberJobIdsForTests(): void {
  clearMemberEnhanceState();
}

/**
 * Drop cached Sora job ids / results / inflight polls.
 * Call on SPA navigation and when subtitleOptimizeModel changes mid-flight.
 * Also bumps an epoch so mid-await poll work from the previous flight aborts.
 */
export function clearMemberEnhanceState(): void {
  memberEnhanceEpoch += 1;
  memberJobIds.clear();
  memberJobResults.clear();
  memberJobInflight.clear();
  asrUploadInflight.clear();
}

/** @deprecated Prefer clearMemberEnhanceState — same implementation. */
export function clearMemberJobIds(): void {
  clearMemberEnhanceState();
}

/** Full-day info cue (member-only / dismiss / progress placeholders). */
export function createMemberServiceInfoCue(text: string): MemberServiceCue[] {
  return [{
    start: 0,
    end: 86400000,
    text,
    info: true,
    segs: [{ start: 0, end: 86400000, text }],
  }];
}

/** Mirrors functions/youtube.js encodeSubs. */
export function encodeMemberSubs(subs: MemberServiceCue[], languageCode?: string): MemberServiceCue[] {
  const out = subs.map((cue) => ({
    ...cue,
    segs: (cue.segs ?? []).map((seg) => ({ ...seg })),
  }));
  for (let i = 0; i < out.length; i += 1) {
    const cue = out[i];
    if (!cue) continue;
    const newSegs: MemberServiceCue['segs'] = [];

    cue.text = cue.segs[0]?.text ?? '';
    const firstSeg = cue.segs[0] as MemberServiceCue['segs'][number] & {
      lang?: string;
      words?: Array<{ text: string; start?: number; end?: number }>;
    };

    if (languageCode || firstSeg?.lang) {
      cue.lang = (languageCode && languageCode.split('-')[0]) || firstSeg?.lang;
    }

    for (let j = 0; j < cue.segs.length; j += 1) {
      const seg = cue.segs[j] as MemberServiceCue['segs'][number] & {
        words?: Array<{ text: string; start?: number; end?: number }>;
      };
      if (seg?.words) {
        for (let z = 0; z < seg.words.length; z += 1) {
          const word = seg.words[z];
          if (!word) continue;
          word.text = word.text.trim();
          newSegs.push(word as MemberServiceCue['segs'][number]);
        }
      }
    }

    if (newSegs.length > 0) {
      cue.segs = newSegs;
    }
  }
  return out;
}

/** Localized dismiss / members_only (extension web_service_dismiss_*). */
export function dismissMessage(desc?: string): string {
  return serviceDismissText(desc);
}

export type FetchMemberServiceOpts = SoraClientOptions & SoraAuthOpts & {
  asrSource?: SoraAsrSource;
  /** Creator enhance language bucket. */
  lc?: string;
};

/**
 * Create/poll Sora job and return legacy MemberServiceResponse shape.
 * `v` = enhance full, `v4` = enhance trial, `v2` = whisper.
 * Captures enhance epoch so mid-await create/poll after clearMemberEnhanceState
 * does not re-seed job ids for a newer flight.
 */
export async function fetchMemberServiceJson(
  endpoint: 'v' | 'v2' | 'v4',
  videoId: string,
  opts: FetchMemberServiceOpts = {},
): Promise<MemberServiceResponse | undefined> {
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  const auth: SoraAuthOpts = {
    isMember: opts.isMember,
    userId: opts.userId,
    userToken: opts.userToken,
  };
  const asrSource = opts.asrSource;
  const languageCode = opts.lc;
  const epoch = memberEnhanceEpoch;
  const stillCurrent = () => epoch === memberEnhanceEpoch;

  try {
    const key = memberJobKey(endpoint, videoId, asrSource, languageCode);
    let jobId = memberJobIds.get(key);

    if (!jobId) {
      const body =
        endpoint === 'v2'
          ? ({ type: 'whisper' as const, videoId })
          : ({
              type: 'enhance' as const,
              videoId,
              tier: endpoint === 'v' ? ('full' as const) : ('trial' as const),
              ...(asrSource === 'creator'
                ? {
                    asrSource: 'creator' as const,
                    lc: normalizeCreatorLc(languageCode),
                  }
                : {}),
            });

      const created = await createJob(body, auth, fetchImpl, opts.baseUrl, opts.apiKey);
      if (!stillCurrent()) return undefined;

      if (created.httpStatus === 200 && created.status === 'done' && created.result != null) {
        return {
          response: 'OK',
          result: created.result as MemberServiceCue[],
        };
      }

      if (created.httpStatus === 403) {
        return { response: 'Failed' };
      }

      const createFail = mapSoraCreateFailureToLegacy(created);
      if (createFail) {
        return { response: createFail.response, desc: createFail.desc };
      }

      if (!created.jobId) {
        return { response: 'Failed' };
      }

      jobId = created.jobId;
      memberJobIds.set(key, jobId);

      if (created.status === 'pending' || created.status === 'queued') {
        return { response: 'Queuing', position: 1 };
      }
    }

    const poll = await getJob(jobId, auth, fetchImpl, opts.baseUrl, opts.apiKey);
    if (!stillCurrent()) return undefined;
    if (!poll) return undefined;

    if (shouldClearCachedSoraJobId(poll) && memberJobIds.get(key) === jobId) {
      memberJobIds.delete(key);
    }

    const legacy = mapSoraJobToLegacy(poll);
    return {
      response: legacy.response,
      result: legacy.result as MemberServiceCue[] | undefined,
      position: legacy.position,
      progress: legacy.progress,
      desc: legacy.desc,
      phase: legacy.phase,
    };
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type PollMemberJobOptions = FetchMemberServiceOpts & {
  endpoint: 'v' | 'v2' | 'v4';
  videoId: string;
  isMember: boolean;
  onProgress?: (message: string) => void;
  maxAttempts?: number;
  /** Return false to abort polling (title / generation change). */
  shouldContinue?: () => boolean;
};

/** Thrown when `shouldContinue` returns false mid-poll. */
export class CaptionJobCancelledError extends Error {
  constructor(message = 'Caption job cancelled.') {
    super(message);
    this.name = 'CaptionJobCancelledError';
  }
}

/** Poll Sora until job completes or fails. Dual-slot callers share one in-flight poll. */
export async function pollMemberJobUntilDone(opts: PollMemberJobOptions): Promise<Cue[]> {
  const {
    endpoint,
    videoId,
    isMember,
    onProgress,
    maxAttempts = 120,
    asrSource,
    lc,
    shouldContinue,
  } = opts;
  const key = memberJobKey(endpoint, videoId, asrSource, lc);
  const epoch = memberEnhanceEpoch;
  const alive = () =>
    epoch === memberEnhanceEpoch && (!shouldContinue || shouldContinue());

  if (endpoint === 'v2' && !isMember) {
    throw new Error('Whisper captions require membership.');
  }

  if (!alive()) {
    throw new CaptionJobCancelledError();
  }

  const cached = memberJobResults.get(key);
  if (cached) return cached.map((cue) => ({ ...cue, segs: cue.segs?.map((s) => ({ ...s })) }));

  const inflight = memberJobInflight.get(key);
  if (inflight) return inflight;

  const run = (async (): Promise<Cue[]> => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (!alive()) {
        throw new CaptionJobCancelledError();
      }

      const json = await fetchMemberServiceJson(endpoint, videoId, opts);
      if (!alive()) {
        throw new CaptionJobCancelledError();
      }
      if (json === undefined) {
        throw new Error('Network error while polling caption job.');
      }

      if (json.response === 'OK' && json.result) {
        const cues = encodeMemberSubs(json.result);
        if (!alive()) {
          throw new CaptionJobCancelledError();
        }
        memberJobResults.set(key, cues);
        return cues;
      }

      if (json.response === 'Queuing') {
        onProgress?.(memberJobProgressText(json));
        await sleep(1000 + Math.floor(Math.random() * 500));
        continue;
      }

      if (json.response === 'Dismiss') {
        throw new Error(dismissMessage(json.desc));
      }

      if (json.response === 'Failed') {
        throw new Error('Caption job failed.');
      }

      throw new Error('Caption job failed.');
    }

    throw new Error('Caption job timed out.');
  })();

  memberJobInflight.set(key, run);
  try {
    return await run;
  } finally {
    // Identity check: clearMemberEnhanceState may have started a newer flight
    // under the same key — do not wipe its coalesce entry.
    if (memberJobInflight.get(key) === run) {
      memberJobInflight.delete(key);
    }
  }
}

/** Extension `uploadAsrBridgePayload` — one in-flight upload per videoId+asrSource(+lc). */
export async function uploadAsrBridgePayload(
  videoId: string,
  payload: unknown,
  languageCode: string,
  fetchImpl: typeof fetch = fetch,
  auth: SoraAuthOpts = {},
  baseUrl?: string,
  apiKey?: string,
  asrSource?: SoraAsrSource,
): Promise<void> {
  const bucket = resolveAsrSourceBucket(asrSource);
  const inflightKey =
    bucket === 'creator'
      ? `${videoId}:${bucket}:${normalizeCreatorLc(languageCode)}`
      : `${videoId}:${bucket}`;
  const existing = asrUploadInflight.get(inflightKey);
  if (existing) return existing;

  let pending!: Promise<void>;
  pending = (async () => {
    try {
      await uploadAsrCache(
        videoId,
        payload,
        languageCode,
        auth,
        fetchImpl,
        baseUrl,
        apiKey,
        asrSource,
      );
    } finally {
      if (asrUploadInflight.get(inflightKey) === pending) {
        asrUploadInflight.delete(inflightKey);
      }
    }
  })();
  asrUploadInflight.set(inflightKey, pending);
  return pending;
}

export { uploadAsrCache };
